import { makeChoices } from "../distractors";
import { clean, frac, lcm, numStr, numUnit, simplify, text } from "../format";
import type { QuestionDraft, Rng, TopicDef } from "../types";
import { both, byTier, fraction, numeric, q } from "./helpers";
import { makeTextChoices } from "../distractors";

const CM2 = { pl: "cm²", en: "cm²" };
const CM3 = { pl: "cm³", en: "cm³" };
const CM = { pl: "cm", en: "cm" };
const cm2 = (n: number) => numUnit(n, "cm²");
const cm3 = (n: number) => numUnit(n, "cm³");
const cm = (n: number) => numUnit(n, "cm");

function fracChoices(rng: Rng, num: number, den: number, others: Array<[number, number]>) {
  const { num: n, den: d } = simplify(num, den);
  const alts = others
    .map(([a, b]) => simplify(a, b))
    .filter((f) => f.den !== 0 && f.num >= 0 && !(f.num === n && f.den === d))
    .map((f) => frac(f.num, f.den));
  return makeTextChoices(rng, frac(n, d), alts, (i) => {
    const f = simplify(n + i + 1, d + i + 2);
    return frac(f.num, f.den);
  });
}

// ---------------------------------------------------------------- 1. equations with fractions
function equationsFractions(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const d = rng.int(2, 6);
      const x = d * rng.int(1, 9);
      const b = rng.int(1, 12);
      const c = x / d + b;
      const choice4 = makeChoices(rng, x, [x / d, (c - b) * d + d, c * d], (r) => r.int(1, 60));
      return q(
        both(`Rozwiąż równanie: x/${d} + ${b} = ${c}`, `Solve: x/${d} + ${b} = ${c}`),
        numeric(x),
        [
          `Przenosimy ${b}: x/${d} = ${c} − ${b} = ${c - b}.`,
          `Mnożymy przez ${d}: x = ${c - b} · ${d} = ${x}.`,
        ],
        [`Move ${b}: x/${d} = ${c} − ${b} = ${c - b}.`, `Multiply by ${d}: x = ${c - b} × ${d} = ${x}.`],
        { choice4 },
      );
    },
    () => {
      const d1 = rng.int(2, 5);
      let d2 = rng.int(2, 6);
      while (d2 === d1) d2 = rng.int(2, 6);
      const L = lcm(d1, d2);
      const x = L * rng.int(1, 6);
      const c = x / d1 + x / d2;
      const choice4 = makeChoices(rng, x, [c, x / 2, x + L], (r) => r.int(2, 80));
      return q(
        both(`Rozwiąż równanie: x/${d1} + x/${d2} = ${c}`, `Solve: x/${d1} + x/${d2} = ${c}`),
        numeric(x),
        [
          `Mnożymy obie strony przez ${L}: ${L / d1}x + ${L / d2}x = ${c * L}.`,
          `${L / d1 + L / d2}x = ${c * L}, więc x = ${x}.`,
        ],
        [
          `Multiply both sides by ${L}: ${L / d1}x + ${L / d2}x = ${c * L}.`,
          `${L / d1 + L / d2}x = ${c * L}, so x = ${x}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const d = rng.int(2, 5);
      const a = rng.int(1, 9);
      const x = rng.int(-6, 12);
      const c = x + a; // (x + a)/d = c/d  →  (x+a)/d = k where k = (x+a)/d must be integer
      const k = Math.floor(c / d);
      const xx = k * d - a;
      const choice4 = makeChoices(rng, xx, [k - a, k * d, xx + 1], (r) => r.int(-10, 20));
      return q(
        both(`Rozwiąż równanie: (x + ${a})/${d} = ${k}`, `Solve: (x + ${a})/${d} = ${k}`),
        numeric(xx),
        [`Mnożymy przez ${d}: x + ${a} = ${k * d}.`, `x = ${k * d} − ${a} = ${xx}.`],
        [`Multiply by ${d}: x + ${a} = ${k * d}.`, `x = ${k * d} − ${a} = ${xx}.`],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 2. inequalities
function inequalities(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const a = rng.int(2, 6);
      const b = rng.int(1, 9);
      const bound = rng.int(3, 12);
      const c = a * bound + b + rng.int(1, a - 1 || 1);
      const best = Math.ceil((c - b) / a) - 1;
      const choice4 = makeChoices(rng, best, [best + 1, best - 1, c - b], (r) => r.int(1, 20));
      return q(
        both(
          `Jaka jest największa liczba całkowita spełniająca nierówność ${a}x + ${b} < ${c}?`,
          `What is the largest integer satisfying ${a}x + ${b} < ${c}?`,
        ),
        numeric(best),
        [
          `${a}x < ${c} − ${b} = ${c - b}, więc x < ${numStr(clean((c - b) / a), "pl")}.`,
          `Największa liczba całkowita mniejsza od ${numStr(clean((c - b) / a), "pl")} to ${best}.`,
        ],
        [
          `${a}x < ${c} − ${b} = ${c - b}, so x < ${numStr(clean((c - b) / a), "en")}.`,
          `The largest integer below ${numStr(clean((c - b) / a), "en")} is ${best}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const a = rng.int(2, 6);
      const b = rng.int(1, 9);
      const c = a * rng.int(2, 10) + b;
      const bound = (c - b) / a;
      const choice4 = makeChoices(rng, bound, [bound - 1, bound + 1, c - b], (r) => r.int(1, 20));
      return q(
        both(
          `Jaka jest najmniejsza liczba całkowita spełniająca nierówność ${a}x − ${b} ≥ ${c - 2 * b}?`,
          `What is the smallest integer satisfying ${a}x − ${b} ≥ ${c - 2 * b}?`,
        ),
        numeric(bound),
        [
          `${a}x ≥ ${c - 2 * b} + ${b} = ${c - b}, więc x ≥ ${bound}.`,
          `Najmniejsza taka liczba całkowita to ${bound}.`,
        ],
        [
          `${a}x ≥ ${c - 2 * b} + ${b} = ${c - b}, so x ≥ ${bound}.`,
          `The smallest such integer is ${bound}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const lo = -rng.int(1, 5);
      const hi = lo + rng.int(3, 8);
      const count = hi - lo - 1;
      const choice4 = makeChoices(rng, count, [count + 1, count + 2, hi - lo + 1], (r) => r.int(1, 12));
      return q(
        both(
          `Ile liczb całkowitych spełnia jednocześnie nierówności x > ${lo} i x < ${hi}?`,
          `How many integers satisfy both x > ${lo} and x < ${hi}?`,
        ),
        numeric(count),
        [
          `Liczby całkowite między ${lo} a ${hi} (bez końców): ${Array.from({ length: count }, (_, i) => lo + 1 + i).join(", ")}.`,
          `Jest ich ${count}.`,
        ],
        [
          `Integers strictly between ${lo} and ${hi}: ${Array.from({ length: count }, (_, i) => lo + 1 + i).join(", ")}.`,
          `There are ${count}.`,
        ],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 3. circle
function circle(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const r = rng.int(1, 12);
      const k = 2 * r;
      const choice4 = makeChoices(rng, k, [r, r * r, 4 * r], (x) => x.int(1, 30));
      return q(
        both(
          `Obwód koła o promieniu ${r} cm wynosi k·π cm. Podaj k.`,
          `The circumference of a circle with radius ${r} cm is k·π cm. Give k.`,
        ),
        numeric(k),
        [`Obwód koła = 2πr.`, `2 · ${r} = ${k}, więc obwód to ${k}π cm.`],
        [`Circumference = 2πr.`, `2 × ${r} = ${k}, so the circumference is ${k}π cm.`],
        { choice4 },
      );
    },
    () => {
      const r = rng.int(1, 12);
      const k = r * r;
      const choice4 = makeChoices(rng, k, [2 * r, r, 4 * r * r], (x) => x.int(1, 150));
      return q(
        both(
          `Pole koła o promieniu ${r} cm wynosi k·π cm². Podaj k.`,
          `The area of a circle with radius ${r} cm is k·π cm². Give k.`,
        ),
        numeric(k),
        [`Pole koła = πr².`, `${r}² = ${k}, więc pole to ${k}π cm².`],
        [`Area = πr².`, `${r}² = ${k}, so the area is ${k}π cm².`],
        { choice4 },
      );
    },
    () => {
      const d = rng.int(2, 20);
      const value = clean(3.14 * d);
      const choice4 = makeChoices(
        rng,
        value,
        [clean(3.14 * d * d), clean(3.14 * (d / 2)), clean(value + 3.14)],
        (x) => clean((x.int(6, 60) * 3.14) / 2),
        cm,
      );
      return q(
        both(
          `Oblicz obwód koła o średnicy ${d} cm. Przyjmij π ≈ 3,14.`,
          `Find the circumference of a circle with diameter ${d} cm. Use π ≈ 3.14.`,
        ),
        { mode: "numeric", value, tolerance: 0.02, unit: CM },
        [`Obwód = π · d (średnica), bo 2r = d.`, `3,14 · ${d} = ${numStr(value, "pl")} cm.`],
        [`Circumference = π × d (diameter), because 2r = d.`, `3.14 × ${d} = ${numStr(value, "en")} cm.`],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 4. symmetry
function symmetry(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  const x = rng.int(-8, 8);
  const y = rng.int(-8, 8);
  return byTier(
    tier,
    () => {
      const choice4 = makeChoices(rng, -y, [y, -x, x], (r) => r.int(-9, 9));
      return q(
        both(
          `Punkt A = (${x}, ${y}) odbito symetrycznie względem osi OX. Podaj współrzędną y obrazu.`,
          `Point A = (${x}, ${y}) is reflected in the x-axis. Give the y coordinate of the image.`,
        ),
        numeric(-y),
        [`Symetria względem osi OX zmienia znak współrzędnej y, x zostaje.`, `Obraz: (${x}, ${-y}).`],
        [`Reflection in the x-axis flips the sign of y; x stays.`, `Image: (${x}, ${-y}).`],
        { choice4 },
      );
    },
    () => {
      const choice4 = makeChoices(rng, -x, [x, -y, y], (r) => r.int(-9, 9));
      return q(
        both(
          `Punkt A = (${x}, ${y}) odbito symetrycznie względem osi OY. Podaj współrzędną x obrazu.`,
          `Point A = (${x}, ${y}) is reflected in the y-axis. Give the x coordinate of the image.`,
        ),
        numeric(-x),
        [`Symetria względem osi OY zmienia znak współrzędnej x, y zostaje.`, `Obraz: (${-x}, ${y}).`],
        [`Reflection in the y-axis flips the sign of x; y stays.`, `Image: (${-x}, ${y}).`],
        { choice4 },
      );
    },
    () => {
      const askX = rng.chance(0.5);
      const v = askX ? -x : -y;
      const choice4 = makeChoices(rng, v, [askX ? x : y, askX ? -y : -x, v + 1], (r) => r.int(-9, 9));
      return q(
        both(
          `Punkt A = (${x}, ${y}) odbito symetrycznie względem początku układu (0, 0). Podaj współrzędną ${askX ? "x" : "y"} obrazu.`,
          `Point A = (${x}, ${y}) is reflected through the origin (0, 0). Give the ${askX ? "x" : "y"} coordinate of the image.`,
        ),
        numeric(v),
        [`Symetria środkowa względem (0, 0) zmienia znaki obu współrzędnych.`, `Obraz: (${-x}, ${-y}).`],
        [`Point reflection through (0, 0) flips both signs.`, `Image: (${-x}, ${-y}).`],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 5. solids
function solids(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const a = rng.int(2, 9);
      const b = rng.int(2, 9);
      const c = rng.int(2, 9);
      const area = 2 * (a * b + b * c + a * c);
      const choice4 = makeChoices(
        rng,
        area,
        [a * b * c, a * b + b * c + a * c, area + a],
        (r) => r.int(20, 400),
        cm2,
      );
      return q(
        both(
          `Prostopadłościan ma krawędzie ${a} cm, ${b} cm i ${c} cm. Jakie jest jego pole powierzchni całkowitej?`,
          `A cuboid has edges ${a} cm, ${b} cm and ${c} cm. What is its total surface area?`,
        ),
        numeric(area, CM2),
        [
          `Sześć ścian: po dwie o polach ${a}·${b}, ${b}·${c} i ${a}·${c}.`,
          `2 · (${a * b} + ${b * c} + ${a * c}) = ${area} cm².`,
        ],
        [
          `Six faces: two each of ${a}×${b}, ${b}×${c} and ${a}×${c}.`,
          `2 × (${a * b} + ${b * c} + ${a * c}) = ${area} cm².`,
        ],
        { choice4 },
      );
    },
    () => {
      const base = rng.int(2, 10);
      const h = rng.int(2, 10);
      const H = rng.int(2, 12);
      const vol = (base * h * H) / 2;
      const choice4 = makeChoices(
        rng,
        vol,
        [base * h * H, (base * h) / 2, vol * 2 + 1],
        (r) => r.int(8, 600),
        cm3,
      );
      return q(
        both(
          `Graniastosłup ma w podstawie trójkąt o podstawie ${base} cm i wysokości ${h} cm, a jego wysokość to ${H} cm. Jaka jest objętość?`,
          `A prism has a triangular base with base ${base} cm and height ${h} cm; the prism is ${H} cm tall. What is its volume?`,
        ),
        numeric(vol, CM3),
        [
          `Objętość graniastosłupa = pole podstawy · wysokość.`,
          `Pole podstawy: ${base} · ${h} : 2 = ${(base * h) / 2}. Objętość: ${(base * h) / 2} · ${H} = ${vol} cm³.`,
        ],
        [
          `Volume of a prism = base area × height.`,
          `Base area: ${base} × ${h} ÷ 2 = ${(base * h) / 2}. Volume: ${(base * h) / 2} × ${H} = ${vol} cm³.`,
        ],
        { choice4 },
      );
    },
    () => {
      const a = rng.int(2, 9);
      const H = 3 * rng.int(1, 5);
      const vol = (a * a * H) / 3;
      const choice4 = makeChoices(rng, vol, [a * a * H, a * H, vol * 3 + 1], (r) => r.int(4, 400), cm3);
      return q(
        both(
          `Ostrosłup ma w podstawie kwadrat o boku ${a} cm, a jego wysokość to ${H} cm. Jaka jest objętość?`,
          `A pyramid has a square base with side ${a} cm and height ${H} cm. What is its volume?`,
        ),
        numeric(vol, CM3),
        [`Objętość ostrosłupa = (pole podstawy · wysokość) : 3.`, `(${a * a} · ${H}) : 3 = ${vol} cm³.`],
        [`Volume of a pyramid = (base area × height) ÷ 3.`, `(${a * a} × ${H}) ÷ 3 = ${vol} cm³.`],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 6. counting
function counting(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const a = rng.int(2, 6);
      const b = rng.int(2, 6);
      const choice4 = makeChoices(rng, a * b, [a + b, a * b + 1, Math.max(a, b)], (r) => r.int(4, 36));
      return q(
        both(
          `Bohater ma ${a} bluzy i ${b} pary spodni. Na ile sposobów może się ubrać?`,
          `The hero has ${a} hoodies and ${b} pairs of trousers. In how many ways can they dress?`,
        ),
        numeric(a * b),
        [`Reguła mnożenia: każdą bluzę można połączyć z każdą parą spodni.`, `${a} · ${b} = ${a * b}.`],
        [`Multiplication rule: every hoodie goes with every pair of trousers.`, `${a} × ${b} = ${a * b}.`],
        { choice4 },
      );
    },
    () => {
      const n = rng.int(3, 5);
      const fact = [1, 1, 2, 6, 24, 120][n] as number;
      const choice4 = makeChoices(rng, fact, [n * n, n * 2, fact / n], (r) => r.int(2, 130));
      return q(
        both(
          `Na ile sposobów można ustawić ${n} różne książki na półce?`,
          `In how many ways can ${n} different books be arranged on a shelf?`,
        ),
        numeric(fact),
        [
          `Pierwsze miejsce: ${n} możliwości, drugie: ${n - 1}, itd.`,
          `${Array.from({ length: n }, (_, i) => n - i).join(" · ")} = ${fact}.`,
        ],
        [
          `First place: ${n} options, second: ${n - 1}, and so on.`,
          `${Array.from({ length: n }, (_, i) => n - i).join(" × ")} = ${fact}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const digits = rng
        .shuffle([1, 2, 3, 4, 5, 6, 7, 8, 9])
        .slice(0, rng.int(3, 5))
        .sort((a, b) => a - b);
      const n = digits.length;
      const count = n * (n - 1);
      const choice4 = makeChoices(rng, count, [n * n, n + n - 1, count + 1], (r) => r.int(4, 30));
      return q(
        both(
          `Ile liczb dwucyfrowych o różnych cyfrach można ułożyć z cyfr ${digits.join(", ")}?`,
          `How many two-digit numbers with different digits can be made from the digits ${digits.join(", ")}?`,
        ),
        numeric(count),
        [
          `Cyfra dziesiątek: ${n} możliwości, cyfra jedności: ${n - 1} (bez powtórzeń).`,
          `${n} · ${n - 1} = ${count}.`,
        ],
        [`Tens digit: ${n} options, ones digit: ${n - 1} (no repeats).`, `${n} × ${n - 1} = ${count}.`],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 7. probability
function probabilityIntro(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const kind = rng.int(0, 2);
      const [fav, all, descPl, descEn] =
        kind === 0
          ? [3, 6, "liczbę parzystą na kostce", "an even number on a die"]
          : kind === 1
            ? [2, 6, "liczbę większą niż 4 na kostce", "a number greater than 4 on a die"]
            : [1, 6, "szóstkę na kostce", "a six on a die"];
      const s = simplify(fav, all);
      const choice4 = fracChoices(rng, fav, all, [
        [fav, all + 1],
        [all - fav, all],
        [1, all],
      ]);
      return q(
        both(
          `Rzucamy kostką. Jakie jest prawdopodobieństwo, że wypadnie ${descPl}? Podaj ułamek.`,
          `A die is rolled. What is the probability of ${descEn}? Give a fraction.`,
        ),
        fraction(s.num, s.den),
        [
          `P = liczba zdarzeń sprzyjających : liczba wszystkich zdarzeń.`,
          `${fav}/${all}${s.den !== all ? ` = ${s.num}/${s.den}` : ""}.`,
        ],
        [
          `P = favourable outcomes ÷ all outcomes.`,
          `${fav}/${all}${s.den !== all ? ` = ${s.num}/${s.den}` : ""}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const red = rng.int(1, 6);
      const blue = rng.int(1, 6);
      const green = rng.int(0, 4);
      const all = red + blue + green;
      const s = simplify(red, all);
      const choice4 = fracChoices(rng, red, all, [
        [red, all - red],
        [blue, all],
        [red, blue],
      ]);
      return q(
        both(
          `W worku są ${red} czerwone, ${blue} niebieskie i ${green} zielone kule. Jakie jest prawdopodobieństwo wylosowania czerwonej?`,
          `A bag holds ${red} red, ${blue} blue and ${green} green balls. What is the probability of drawing a red one?`,
        ),
        fraction(s.num, s.den),
        [
          `Wszystkich kul: ${red} + ${blue} + ${green} = ${all}. Czerwonych: ${red}.`,
          `P = ${red}/${all}${s.den !== all ? ` = ${s.num}/${s.den}` : ""}.`,
        ],
        [
          `All balls: ${red} + ${blue} + ${green} = ${all}. Red: ${red}.`,
          `P = ${red}/${all}${s.den !== all ? ` = ${s.num}/${s.den}` : ""}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const heads = rng.int(0, 2);
      const fav = [1, 2, 1][heads] as number;
      const s = simplify(fav, 4);
      const descPl = ["dwa orły", "dokładnie jednego orła", "dwie reszki"][heads];
      const descEn = ["two heads", "exactly one head", "two tails"][heads];
      const choice4 = fracChoices(rng, fav, 4, [
        [1, 2],
        [1, 3],
        [3, 4],
      ]);
      return q(
        both(
          `Rzucamy dwiema monetami. Jakie jest prawdopodobieństwo, że wypadną ${descPl}?`,
          `Two coins are tossed. What is the probability of ${descEn}?`,
        ),
        fraction(s.num, s.den),
        [
          `Wszystkie wyniki: OO, OR, RO, RR — 4 możliwości.`,
          `Sprzyjające: ${fav}, więc P = ${fav}/4${s.den !== 4 ? ` = ${s.num}/${s.den}` : ""}.`,
        ],
        [
          `All outcomes: HH, HT, TH, TT — 4 possibilities.`,
          `Favourable: ${fav}, so P = ${fav}/4${s.den !== 4 ? ` = ${s.num}/${s.den}` : ""}.`,
        ],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 8. statistics
function statistics(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const mode = rng.int(1, 6);
      const nums = rng.shuffle([mode, mode, mode, ...Array.from({ length: 4 }, () => rng.int(1, 6))]);
      const counts = new Map<number, number>();
      for (const n of nums) counts.set(n, (counts.get(n) ?? 0) + 1);
      const best = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]![0];
      const choice4 = makeChoices(
        rng,
        best,
        [Math.round(nums.reduce((a, b) => a + b, 0) / nums.length), best + 1, best - 1],
        (r) => r.int(1, 6),
      );
      return q(
        both(
          `Oceny z kartkówek: ${nums.join(", ")}. Jaka jest dominanta (najczęstsza ocena)?`,
          `Quiz grades: ${nums.join(", ")}. What is the mode (most frequent grade)?`,
        ),
        numeric(best),
        [
          `Liczymy, ile razy występuje każda ocena.`,
          `Najczęściej (${counts.get(best)} razy) występuje ${best}.`,
        ],
        [`Count how often each grade appears.`, `${best} appears most often (${counts.get(best)} times).`],
        { choice4 },
      );
    },
    () => {
      const mean = rng.int(3, 20);
      const devs = Array.from({ length: 5 }, () => rng.int(-3, 3));
      devs.push(-devs.reduce((a, b) => a + b, 0));
      const nums = rng.shuffle(devs.map((d) => mean + d));
      const sum = nums.reduce((a, b) => a + b, 0);
      const choice4 = makeChoices(rng, mean, [mean + 1, mean - 1, Math.round(sum / 5)], (r) => r.int(2, 25));
      return q(
        both(`Oblicz średnią liczb: ${nums.join(", ")}.`, `Find the mean of: ${nums.join(", ")}.`),
        numeric(mean),
        [`Suma: ${sum}. Liczb jest ${nums.length}.`, `${sum} : ${nums.length} = ${mean}.`],
        [`Sum: ${sum}. There are ${nums.length} numbers.`, `${sum} ÷ ${nums.length} = ${mean}.`],
        { choice4 },
      );
    },
    () => {
      const n = 4;
      const mean = rng.int(10, 30);
      const known = Array.from({ length: n - 1 }, () => mean + rng.int(-6, 6));
      const missing = mean * n - known.reduce((a, b) => a + b, 0);
      const choice4 = makeChoices(rng, missing, [mean, mean * n, missing + 2], (r) => r.int(5, 50));
      return q(
        both(
          `Średnia ${n} liczb wynosi ${mean}. Trzy z nich to ${known.join(", ")}. Jaka jest czwarta liczba?`,
          `The mean of ${n} numbers is ${mean}. Three of them are ${known.join(", ")}. What is the fourth?`,
        ),
        numeric(missing),
        [
          `Suma wszystkich: ${mean} · ${n} = ${mean * n}. Suma znanych: ${known.reduce((a, b) => a + b, 0)}.`,
          `${mean * n} − ${known.reduce((a, b) => a + b, 0)} = ${missing}.`,
        ],
        [
          `Total: ${mean} × ${n} = ${mean * n}. Known sum: ${known.reduce((a, b) => a + b, 0)}.`,
          `${mean * n} − ${known.reduce((a, b) => a + b, 0)} = ${missing}.`,
        ],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 9. similar figures
function similarFigures(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const a = rng.int(2, 9);
      const k = rng.int(2, 4);
      const choice4 = makeChoices(rng, a * k, [a + k, a * k + 1, a * (k + 1)], (r) => r.int(2, 40), cm);
      return q(
        both(
          `Trójkąt powiększono w skali ${k}. Bok, który miał ${a} cm, ma teraz ile cm?`,
          `A triangle is enlarged by a factor of ${k}. A side that was ${a} cm is now how long?`,
        ),
        numeric(a * k, CM),
        [`Skala ${k} oznacza, że każdy bok jest ${k} razy dłuższy.`, `${a} · ${k} = ${a * k} cm.`],
        [`A factor of ${k} means every side is ${k} times longer.`, `${a} × ${k} = ${a * k} cm.`],
        { choice4 },
      );
    },
    () => {
      const a = rng.int(2, 8);
      const b = rng.int(2, 8);
      const k = rng.int(2, 4);
      const choice4 = makeChoices(rng, b * k, [a * k, b + k, b * k + 1], (r) => r.int(2, 40), cm);
      return q(
        both(
          `Trójkąty są podobne. W pierwszym boki mają ${a} cm i ${b} cm. W drugim bok odpowiadający ${a} cm ma ${a * k} cm. Ile ma bok odpowiadający ${b} cm?`,
          `Two triangles are similar. The first has sides ${a} cm and ${b} cm. In the second, the side matching ${a} cm is ${a * k} cm. How long is the side matching ${b} cm?`,
        ),
        numeric(b * k, CM),
        [`Skala podobieństwa: ${a * k} : ${a} = ${k}.`, `${b} · ${k} = ${b * k} cm.`],
        [`Scale factor: ${a * k} ÷ ${a} = ${k}.`, `${b} × ${k} = ${b * k} cm.`],
        { choice4 },
      );
    },
    () => {
      const a = rng.int(2, 6);
      const b = rng.int(2, 6);
      const k = rng.int(2, 4);
      const area = a * b * k * k;
      const choice4 = makeChoices(rng, area, [a * b * k, a * b, area / k], (r) => r.int(4, 300), cm2);
      return q(
        both(
          `Prostokąt ${a} cm × ${b} cm powiększono w skali ${k}. Jakie jest pole powiększonego prostokąta?`,
          `A ${a} cm × ${b} cm rectangle is enlarged by a factor of ${k}. What is the area of the enlarged rectangle?`,
        ),
        numeric(area, CM2),
        [
          `Boki: ${a * k} cm i ${b * k} cm (pole rośnie ${k * k} razy).`,
          `${a * k} · ${b * k} = ${area} cm².`,
        ],
        [
          `Sides: ${a * k} cm and ${b * k} cm (the area grows ${k * k} times).`,
          `${a * k} × ${b * k} = ${area} cm².`,
        ],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 10. exam mix
function examMix(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const d = rng.pick([3, 4, 5, 8]);
      const n = rng.int(1, d - 1);
      const total = d * rng.int(10, 60);
      const value = (total * n) / d;
      const choice4 = makeChoices(rng, value, [total - value, total / d, value + d], (r) => r.int(10, 300));
      return q(
        both(
          `W szkole jest ${total} uczniów. ${n}/${d} z nich jeździ do szkoły rowerem. Ilu uczniów jeździ rowerem?`,
          `A school has ${total} pupils. ${n}/${d} of them cycle to school. How many pupils cycle?`,
        ),
        numeric(value),
        [`${total} : ${d} = ${total / d}.`, `${total / d} · ${n} = ${value} uczniów.`],
        [`${total} ÷ ${d} = ${total / d}.`, `${total / d} × ${n} = ${value} pupils.`],
        { choice4 },
      );
    },
    () => {
      const price = rng.int(5, 40) * 10;
      const p = rng.pick([10, 20, 25, 30]);
      const count = rng.int(2, 5);
      const each = price - (price * p) / 100;
      const total = each * count;
      const choice4 = makeChoices(
        rng,
        total,
        [price * count, each, total + price],
        (r) => r.int(5, 200) * 10,
        (n) => numUnit(n, "zł"),
      );
      return q(
        both(
          `Gra kosztuje ${price} zł. W promocji jest tańsza o ${p}%. Ile kosztuje ${count} takich gier w promocji?`,
          `A game costs ${price} zł. On sale it is ${p}% cheaper. How much do ${count} games cost on sale?`,
        ),
        numeric(total, { pl: "zł", en: "zł" }),
        [`Cena w promocji: ${price} − ${p}% = ${each} zł.`, `${count} · ${each} = ${total} zł.`],
        [`Sale price: ${price} − ${p}% = ${each} zł.`, `${count} × ${each} = ${total} zł.`],
        { choice4 },
      );
    },
    () => {
      const v1 = rng.pick([40, 50, 60, 80]);
      const t1 = rng.int(1, 3);
      const v2 = rng.pick([60, 70, 90, 100]);
      const t2 = rng.int(1, 3);
      const dist = v1 * t1 + v2 * t2;
      const avg = dist / (t1 + t2);
      const value = clean(avg);
      const choice4 = makeChoices(
        rng,
        value,
        [(v1 + v2) / 2, dist, clean(value + 5)],
        (r) => r.int(40, 100),
        (n) => numUnit(n, "km/h"),
      );
      return q(
        both(
          `Samochód jechał ${t1} h z prędkością ${v1} km/h, a potem ${t2} h z prędkością ${v2} km/h. Jaka była średnia prędkość na całej trasie?`,
          `A car drove ${t1} h at ${v1} km/h, then ${t2} h at ${v2} km/h. What was the average speed for the whole trip?`,
        ),
        { mode: "numeric", value, tolerance: 0.01, unit: { pl: "km/h", en: "km/h" } },
        [
          `Droga: ${v1}·${t1} + ${v2}·${t2} = ${dist} km. Czas: ${t1 + t2} h.`,
          `Średnia prędkość = droga : czas = ${dist} : ${t1 + t2} = ${numStr(value, "pl")} km/h.`,
        ],
        [
          `Distance: ${v1}×${t1} + ${v2}×${t2} = ${dist} km. Time: ${t1 + t2} h.`,
          `Average speed = distance ÷ time = ${dist} ÷ ${t1 + t2} = ${numStr(value, "en")} km/h.`,
        ],
        { choice4 },
      );
    },
  );
}

const def = (
  id: string,
  pl: string,
  en: string,
  generate: (rng: Rng, tier: 1 | 2 | 3) => QuestionDraft,
  modes: TopicDef["modes"] = ["numeric", "choice4"],
): TopicDef => ({
  id,
  level: "sp8",
  name: text(pl, en),
  modes,
  generate,
});

export const SP8_TOPICS: readonly TopicDef[] = [
  def("sp8.equations_fractions", "Równania z ułamkami", "Equations with fractions", equationsFractions),
  def("sp8.inequalities", "Nierówności", "Inequalities", inequalities),
  def("sp8.circle", "Koło i okrąg", "Circles", circle),
  def("sp8.symmetry", "Symetria", "Symmetry", symmetry),
  def("sp8.solids", "Bryły", "Solids", solids),
  def("sp8.counting", "Kombinatoryka", "Counting", counting),
  def("sp8.probability_intro", "Prawdopodobieństwo", "Probability", probabilityIntro, [
    "fraction",
    "choice4",
  ]),
  def("sp8.statistics", "Statystyka", "Statistics", statistics),
  def("sp8.similar_figures", "Figury podobne", "Similar figures", similarFigures),
  def("sp8.exam_mix", "Zadania egzaminacyjne", "Exam-style problems", examMix),
];
